import React, { useRef, useMemo, useState, useEffect } from 'react';
import { uniqBy } from 'lodash-es';
import ChartsIndex from '@intelligence/components/ChartsComponents/ChartsIndex';
import useMultiDataSourceBarCharts from '../../_hooks/useMultiDataSourceBarCharts';
import useMultiDataSourceLineCharts from '../../_hooks/useMultiDataSourceLineCharts';
import useMultiDataSourcePieCharts from '../../_hooks/useMultiDataSourcePieCharts';
import { generateMetricKey, generateMetricRatioKey, getStdAUMetricKey } from '@intelligence/v3/utils/metric';
import { DATA_SOURCE_NAME_MAP } from '@intelligence/pages/TopCharts/_config/topChartsConst';

import type { IChartsIndexInstance } from '@intelligence/components/ChartsComponents/ChartsIndex';

const getFullMetrics = (params?: {
  dataSources?: string[] | null;
  metrics?: string[] | null;
  currentMetrics?: any[];
}) => {
  const fullMetrics = [];
  const { dataSources, metrics, currentMetrics } = params || {};
  if (!Array.isArray(dataSources) || !dataSources.length) return [];
  if (!Array.isArray(metrics) || !metrics.length) return [];

  for (const dataSource of dataSources) {
    for (const metric of metrics) {
      const currentMetric = currentMetrics?.find((item) => {
        const { source: itemDataSource, metric: itemMetric } = item || {};
        return dataSource === itemDataSource && getStdAUMetricKey(metric) === itemMetric;
      });
      const currentMetricTmp = currentMetric || {
        b_predict: false,
        dod: null,
        growth_ratio: null,
        source: dataSource,
        metric,
        name: '',
        value: null,
      };
      fullMetrics.push(currentMetricTmp);
    }
  }

  return fullMetrics;
};

// 每个榜单，组装好各类可视化需要的数据，传递给可视化统一组件
const MultiDataSourceTopChartsVisualis: React.FC<any> = (props: any) => {
  const {
    primaryKey,
    dataSources,
    fetchParams,
    barOrPieData,
    lineData,
    sourceOrders,
    echartsData,
    dateType,
    isVisualisLoading,
    chartType = 'bar',
    isDataBrain,
    titleNow = '',
    topChartsType = '',
    isNodata,
    imageType,
    gameHighId = '',
    isStudio = false,
    metrics,
    metric: metricProps,
    dates,
  } = props;

  const chartsInstance = useRef<IChartsIndexInstance>(null);

  const dataSourceNames = useMemo(() => {
    if (!Array.isArray(dataSources)) return [];
    return dataSources.map((item) => DATA_SOURCE_NAME_MAP[item]).filter((item) => item);
  }, [dataSources]);

  const [chartsData, setChartsData] = useState<any>({
    metricChartlist: [],
    timelineOfMetricChartlist: [],
  });

  const getSortedMetrics = (metrics: any[]): any[] => {
    if (!Array.isArray(metrics)) return [];
    return metrics.sort((a: any, b: any): number => {
      const aIndex = sourceOrders.indexOf(a.source);
      const bIndex = sourceOrders.indexOf(b.source);
      return aIndex - bIndex;
    });
  };

  const getBarOrPieDataOfComponent = (
    games: any[],
    options: {
      metrics: any[];
    },
  ) => {
    if (!Array.isArray(games) || games.length === 0) return [];
    const { metrics } = options || {};
    const result: any[] = [];

    for (let i = 0; i < games.length; i++) {
      const game = games[i];
      const gameId = game?.[primaryKey] || '';
      const gameName = game?.game_name || '';
      let newGame = null;
      const newMetricValueList = [];
      if (!game || !gameId) continue;
      // 按数据源与指标名称重新排序，再下传
      for (const metricData of metrics) {
        const { dataSource, metric } = metricData || {};
        if (
          !metricData ||
          !dataSource ||
          !metric ||
          !fetchParams?.data_sources?.includes(dataSource) ||
          metric !== metricProps
        ) {
          continue;
        }
        const metricValueKey = generateMetricKey({ dataSource, metric });
        const metricRatioValueKey = generateMetricRatioKey(metricValueKey);
        const newMetricValue = {
          titleNow,
          b_predict: false,
          order_source: dataSource,
          order_metric: `${dataSource}_${metric}`,
          source: dataSource,
          metric,
          value: Number.parseFloat(game?.[metricValueKey]),
          growth_ratio: Number.parseFloat(game?.[metricRatioValueKey]),
          linkValue: Number.NaN,
        };
        newMetricValueList.push(newMetricValue);
      }
      newGame = {
        ...game,
        id: gameId,
        imageUrl: game?.cover,
        name: gameName,
        rank_metric: gameName,
        metric_value_list: getSortedMetrics(newMetricValueList),
        metric_list: getFullMetrics({
          dataSources: fetchParams?.data_sources,
          metrics: fetchParams?.metrics?.slice(0, 1),
          currentMetrics: newMetricValueList,
        }),
      };
      result.push(newGame);
    }

    return result;
  };

  const getLineDataOfComponent = (
    games: any,
    options: {
      metrics: any[];
      dates: string[];
    },
  ): any[] => {
    const { metrics, dates } = options || {};
    const result: any[] = [];
    if (!Array.isArray(games) || games.length === 0) return result;
    if (!Array.isArray(dates) || dates.length === 0) return result;

    for (const game of uniqBy(games, primaryKey)) {
      if (!game) continue;
      const gameId = game?.[primaryKey] || '';
      const gameName = game?.game_name || '';

      for (const date of dates) {
        const data = games.find((item: any) => item?.date === date && item?.[primaryKey] === gameId);
        const metricValueList = metrics
          ?.filter((metricData) => {
            const { dataSource, metric } = metricData || {};
            return (
              !!metricData &&
              !!dataSource &&
              !!metric &&
              !!fetchParams?.data_sources?.includes(dataSource) &&
              metric === metricProps
            );
          })
          ?.map((metricData) => {
            const { dataSource, metric } = metricData || {};
            const metricValueKey = generateMetricKey({ dataSource, metric });
            const metricRatioValueKey = generateMetricRatioKey(metricValueKey);

            return {
              b_predict: false,
              metric: dataSource,
              order_source: dataSource,
              order_metric: `${dataSource}_${metric}`,
              value: data?.[metricValueKey],
              growth_ratio: data?.[metricRatioValueKey],
              titleNow,
            };
          });

        result.push({
          id: gameId,
          game_id: gameId,
          name: gameName,
          cover: game?.cover || '',
          time: date,
          rank_name: gameName,
          metric_value_list: getSortedMetrics(metricValueList),
        });
      }
    }

    return result;
  };

  const lenten = useMemo(() => {
    return chartsData.metricChartlist
      ? chartsData.metricChartlist.filter((item: any, index: any) => index < 10)
      : chartsData.metric_chart_list.filter((item: any, index: any) => index < 10);
  }, [chartsData]);

  // 获取柱状图hooks
  const { barOption, showBarPeiName } = useMultiDataSourceBarCharts({
    sendEchartsData: chartsData,
    loading: isVisualisLoading,
    dataSourceEachers: dataSourceNames,
    isDataBrain,
    lenten,
    titleNow,
    topChartsType,
    startDate: fetchParams.start_date,
    endDate: fetchParams.end_date,
    gameHighId,
    isStudio,
  });

  const chartLoading = useMemo(() => {
    return !(barOption || isNodata);
  }, [barOption, isNodata]);

  // 获取折线图hooks
  const { lineOption } = useMultiDataSourceLineCharts({
    sendEchartsData: chartsData,
    loading: isVisualisLoading || chartLoading,
    dataSourceEachers: dataSourceNames,
    titleNow,
    topChartsType,
    imageType,
    chartType,
    dateType,
    startDate: fetchParams.start_date,
    endDate: fetchParams.end_date,
    chartsInstance,
    gameHighId,
    isStudio,
  });

  // 获取饼图hooks
  const { pieOption } = useMultiDataSourcePieCharts({
    chartType,
    chartsInstance,
    sendEchartsData: chartsData,
    loading: isVisualisLoading,
    dataSourceEachers: dataSourceNames,
    lenten,
    titleNow,
    startDate: fetchParams?.start_date,
    endDate: fetchParams?.end_date,
    gameHighId,
    isStudio,
  });

  useEffect(() => {
    const barOrPieDataOfComponent = getBarOrPieDataOfComponent(barOrPieData, { metrics });
    const lineDataOfComponent = getLineDataOfComponent(lineData, {
      metrics,
      dates,
    });
    setChartsData({
      ...chartsData,
      metricChartlist: barOrPieDataOfComponent,
      timelineOfMetricChartlist: lineDataOfComponent,
    });
  }, [barOrPieData, lineData, titleNow, metrics, dates]);

  return (
    <div className="card-line">
      <ChartsIndex
        ref={chartsInstance}
        // @ts-ignore
        echartsData={echartsData || {}}
        dataSourceEachers={dataSourceNames}
        chartLoading={false}
        barOption={barOption}
        lineOption={lineOption}
        pieOption={pieOption}
        chartType={chartType}
        showBarPeiName={showBarPeiName}
        isNodata={isNodata}
      />
    </div>
  );
};

export default MultiDataSourceTopChartsVisualis;
